export const MAX_MESSAGE_BYTES = 1024 * 1024;
export const TOKEN_RE = /^[a-zA-Z0-9_-]{32,128}$/;
export const ROOM_RE = /^[a-zA-Z0-9_-]{16,80}$/;
export const ROLES = ["operator", "talent", "remote"];
const object = (x) => !!x && typeof x === "object" && !Array.isArray(x);
const finite = (x, min, max) =>
  typeof x === "number" && Number.isFinite(x) && x >= min && x <= max;
const string = (x, max) => typeof x === "string" && x.length <= max;
const enums = {
  alignment: ["left", "center", "right", "justify"],
  direction: ["ltr", "rtl", "auto"],
  mode: ["manual", "constant", "timed", "voice", "sound"],
  speechEngine: ["browser", "local", "streaming", "external"],
  streamingModel: ["small", "tiny"],
  theme: ["dark", "light"],
  layout: ["studio", "focus", "compact"],
  cueLayer: ["front", "behind"],
};
const numeric = {
  outputWidth: [320, 7680],
  outputHeight: [240, 4320],
  fontSize: [8, 300],
  lineHeight: [0.5, 5],
  margin: [0, 49],
  readingLine: [0, 100],
  guideOpacity: [0, 1],
  cueOpacity: [0, 1],
  wpm: [0, 1000],
  duration: [0, 86400],
  countdown: [0, 3600],
  confidenceThreshold: [0, 1],
  manualOverrideMs: [0, 600000],
};
// This operator-only preference was introduced after 1.1.x. Accept its absence
// in older stored snapshots; transports omit it when talking to older relays.
const optionalNumeric = { speedStep: [1, 100] };
// Newly added settings are optional on the wire so pre-upgrade snapshots and
// reconnecting displays remain valid. When present, they use the same bounds.
const optionalSettings = new Set(["speedStep", "streamingModel", "cueOpacity", "cueLayer"]);
const bool = [
  "smoothScroll",
  "highlightCurrent",
  "flipX",
  "flipY",
  "showGuide",
  "showPast",
  "keepAwake",
];
const strings = {
  fontFamily: 160,
  foreground: 80,
  background: 80,
  language: 80,
};
export function validSettings(x, partial = false) {
  if (!object(x)) return false;
  const keys = [
    ...Object.keys(enums),
    ...Object.keys(numeric),
    ...bool,
    ...Object.keys(strings),
    ...Object.keys(optionalNumeric),
  ];
  if (Object.keys(x).some((k) => !keys.includes(k))) return false;
  if (!partial && keys.some((k) => !optionalSettings.has(k) && !(k in x))) return false;
  return Object.entries(x).every(([k, v]) =>
    enums[k]
      ? enums[k].includes(v)
      : numeric[k]
        ? finite(v, ...numeric[k])
        : optionalNumeric[k]
          ? finite(v, ...optionalNumeric[k]) && Number.isInteger(v)
        : bool.includes(k)
          ? typeof v === "boolean"
          : string(v, strings[k]),
  );
}
/** Cue artwork is a bundled identifier, not executable markup or an asset URL. */
export function validVisualCue(cue) {
  if (!object(cue) || Object.keys(cue).some((key) =>
    !["id", "text", "picture", "color", "layout", "expiresAt"].includes(key))) return false;
  return string(cue.id, 80) && /^[a-zA-Z0-9_-]+$/.test(cue.id) &&
    string(cue.text, 500) &&
    string(cue.picture, 64) && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(cue.picture) &&
    typeof cue.color === "string" && /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(cue.color) &&
    ["card", "banner"].includes(cue.layout) &&
    (cue.expiresAt === null || finite(cue.expiresAt, 0, 1e16));
}
function validCueAppearance(value) {
  return object(value) && Object.keys(value).every(key => ["opacity", "layer"].includes(key)) &&
    finite(value.opacity, 0, 1) && ["front", "behind"].includes(value.layer);
}
export function validCommand(c) {
  if (!object(c) || !string(c.type, 20)) return false;
  const permitted = {
    play: [],
    pause: [],
    toggle: [],
    reset: [],
    seek: ["wordIndex"],
    nudge: ["words"],
    speed: ["wpm"],
    blackout: ["value"],
    emergency: ["text"],
    cue: ["cue", "pause"],
    settings: ["settings"],
  };
  if (
    !Object.hasOwn(permitted, c.type) ||
    Object.keys(c).some((k) => k !== "type" && !permitted[c.type].includes(k))
  )
    return false;
  if (c.type === "seek")
    return finite(c.wordIndex, 0, 1000000) && Number.isInteger(c.wordIndex);
  if (c.type === "nudge")
    return finite(c.words, -1000000, 1000000) && Number.isInteger(c.words);
  if (c.type === "speed") return finite(c.wpm, 1, 1000);
  if (c.type === "blackout")
    return c.value === undefined || typeof c.value === "boolean";
  if (c.type === "emergency") return string(c.text, 1000);
  if (c.type === "cue") return (c.cue === null || validVisualCue(c.cue)) &&
    (c.pause === undefined || typeof c.pause === "boolean");
  if (c.type === "settings") return validSettings(c.settings, true);
  return true;
}
export function validState(s) {
  if (!object(s) || !object(s.script) || !validSettings(s.settings))
    return false;
  if (s.cueAppearance !== undefined && !validCueAppearance(s.cueAppearance)) return false;
  const c = s.script;
  if (
    !string(c.id, 200) ||
    !string(c.title, 300) ||
    !string(c.html, 400000) ||
    !string(c.notes, 100000) ||
    !finite(c.updatedAt, 0, 1e16) ||
    !Array.isArray(c.tags) ||
    c.tags.length > 100 ||
    !c.tags.every((t) => string(t, 100))
  )
    return false;
  // Inspect markup only: scripts can legitimately discuss onclick= or javascript: as visible text.
  const tags = c.html.match(/<[^>]*>/g) || [];
  if (
    tags.some((tag) =>
      /<\s*(script|iframe|object|embed|svg|math|style|link|meta|base)\b|\s+on\w+\s*=|(?:javascript|vbscript)\s*:/i.test(
        tag,
      ),
    )
  )
    return false;
  return (
    finite(s.wordProgress, 0, 1) &&
    finite(s.wordIndex, 0, 1000000) &&
    Number.isInteger(s.wordIndex) &&
    typeof s.playing === "boolean" &&
    typeof s.blackout === "boolean" &&
    string(s.emergency, 1000) &&
    (s.visualCue === undefined || s.visualCue === null || validVisualCue(s.visualCue)) &&
    finite(s.elapsed, 0, 1e12) &&
    (s.startedAt === null || finite(s.startedAt, 0, 1e16)) &&
    (s.countdownUntil === null || finite(s.countdownUntil, 0, 1e16)) &&
    finite(s.revision, 0, Number.MAX_SAFE_INTEGER) &&
    Number.isInteger(s.revision)
  );
}
export function decodeMessage(raw) {
  try {
    if (
      typeof raw !== "string" ||
      new TextEncoder().encode(raw).length > MAX_MESSAGE_BYTES
    )
      return null;
    const m = JSON.parse(raw);
    return object(m) && string(m.type, 30) ? m : null;
  } catch {
    return null;
  }
}

export function validPosition(p) {
  if (
    !object(p) ||
    Object.keys(p).some(
      (k) =>
        ![
          "wordProgress",
          "wordIndex",
          "playing",
          "startedAt",
          "elapsed",
          "blackout",
          "emergency",
          "visualCue",
          "countdownUntil",
          "revision",
        ].includes(k),
    )
  )
    return false;
  return (
    finite(p.wordProgress, 0, 1) &&
    finite(p.wordIndex, 0, 1000000) &&
    Number.isInteger(p.wordIndex) &&
    typeof p.playing === "boolean" &&
    typeof p.blackout === "boolean" &&
    string(p.emergency, 1000) &&
    (p.visualCue === undefined || p.visualCue === null || validVisualCue(p.visualCue)) &&
    finite(p.elapsed, 0, 1e12) &&
    (p.startedAt === null || finite(p.startedAt, 0, 1e16)) &&
    (p.countdownUntil === null || finite(p.countdownUntil, 0, 1e16)) &&
    finite(p.revision, 0, Number.MAX_SAFE_INTEGER) &&
    Number.isInteger(p.revision)
  );
}
